import { useState } from "react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, CalendarDays, Users2, Luggage, Compass, ArrowRight, Sparkles } from "lucide-react";
import { getDestinations, getPackages } from "../lib/api";
import { track } from "../lib/track";
import { Reveal } from "./Reveal";

const DURATIONS = ["3-4 Hari", "5-6 Hari", "7+ Hari", "Fleksibel"];
const TRIP_TYPES = ["Private Trip", "Group Trip", "Family Trip", "Honeymoon", "Corporate", "Study Tour", "Halal Trip"];

export default function TripFinder() {
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [results, setResults] = useState(null);
  const [form, setForm] = useState({ destination: "", date: "", duration: "", pax: "", tripType: "" });

  useEffect(() => {
    getDestinations().then(setDestinations).catch(() => {});
    getPackages().then(setPackages).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const find = (e) => {
    e.preventDefault();
    track("find_trip", { destination: form.destination });
    let matches = packages;
    if (form.destination) matches = matches.filter((p) => p.destination_slug === form.destination);
    if (form.duration === "3-4 Hari") matches = matches.filter((p) => ["3D2N", "4D3N"].includes(p.duration));
    if (form.duration === "5-6 Hari") matches = matches.filter((p) => ["5D4N", "6D5N"].includes(p.duration));
    if (form.duration === "7+ Hari") matches = matches.filter((p) => !["3D2N", "4D3N", "5D4N", "6D5N"].includes(p.duration));
    setResults(matches);
  };

  return (
    <div className="relative z-20 mx-auto -mt-14 max-w-5xl px-5 sm:px-8">
      <Reveal>
        <div className="glass rounded-3xl p-5 shadow-elevated sm:p-7" data-testid="trip-finder-panel">
          <h3 className="font-heading text-xl font-extrabold text-charcoal sm:text-2xl">
            Mau Liburan ke <span className="text-tosca-600">Mana?</span>
          </h3>
          <form onSubmit={find} className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div className="col-span-2 sm:col-span-1 lg:col-span-2">
              <label className="label"><Compass className="mr-1 inline h-3 w-3" />Destination</label>
              <select className="field" value={form.destination} onChange={set("destination")} data-testid="trip-finder-destination-select">
                <option value="">Semua destinasi</option>
                {destinations.map((d) => (
                  <option key={d.slug} value={d.slug}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label"><CalendarDays className="mr-1 inline h-3 w-3" />Travel Date</label>
              <input type="date" className="field" value={form.date} onChange={set("date")} data-testid="trip-finder-date-input" />
            </div>
            <div>
              <label className="label"><Luggage className="mr-1 inline h-3 w-3" />Duration</label>
              <select className="field" value={form.duration} onChange={set("duration")} data-testid="trip-finder-duration-select">
                <option value="">Fleksibel</option>
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label"><Users2 className="mr-1 inline h-3 w-3" />Travelers</label>
              <input className="field" placeholder="cth. 4" value={form.pax} onChange={set("pax")} data-testid="trip-finder-pax-input" />
            </div>
            <div className="flex items-end">
              <button type="submit" className="btn btn-gold w-full" data-testid="trip-finder-submit-button">
                <Search className="h-4 w-4" /> Find My Trip
              </button>
            </div>
          </form>

          {results && (
            <div className="mt-5 border-t border-slate-200 pt-5" data-testid="trip-finder-results">
              {results.length > 0 ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {results.length} paket direkomendasikan
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {results.slice(0, 3).map((p) => (
                      <Link
                        key={p.slug}
                        to={`/land-arrangement/${p.slug}`}
                        data-testid={`trip-finder-result-${p.slug}`}
                        onClick={() => track("booking_intent", { package: p.slug })}
                        className="group flex items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white p-3.5 transition hover:border-tosca-600 hover:shadow-subtle"
                      >
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-tosca-600">{p.destination_name} · {p.duration}</p>
                          <p className="mt-0.5 text-sm font-bold text-charcoal">{p.title}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-300 transition group-hover:translate-x-1 group-hover:text-tosca-600" />
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-start gap-3 rounded-2xl bg-gold-light p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="trip-finder-no-match">
                  <div>
                    <p className="font-heading text-base font-bold text-charcoal">Tidak menemukan paket yang sesuai?</p>
                    <p className="mt-0.5 text-sm text-slate-500">Tim kami siap menyusun itinerary custom sesuai kebutuhan Anda.</p>
                  </div>
                  <Link to="/custom-trip" className="btn btn-primary !py-2.5 !text-[13px]" data-testid="trip-finder-custom-trip-cta">
                    <Sparkles className="h-4 w-4" /> Request Custom Trip
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </Reveal>
    </div>
  );
}
